/**
 * Canonical lock provider registry for OpenCare.
 * oc_provider (Dataverse choice) maps to these ids; oc_vendor is legacy fallback.
 */

export const PROVIDER_OPTION_VALUES = {
  manual: 161730003,
  bekey: 161730004,
  unloc: 161730005,
  puttekasse: 161730006,
  danalock: 161730007,
  swedlock: 161730008,
  other: 161730009
};

const OPTION_VALUE_TO_ID = Object.fromEntries(
  Object.entries(PROVIDER_OPTION_VALUES).map(([id, value]) => [value, id])
);

const VENDOR_ALIASES = {
  manual: ['manual', 'ukjent', 'unknown', 'elektronisk', 'mekanisk'],
  bekey: ['bekey', 'be-key', 'be key'],
  unloc: ['unloc'],
  puttekasse: ['puttekasse', 'putte kasse'],
  danalock: ['danalock', 'dana lock'],
  swedlock: ['swedlock', 'swed lock'],
  other: ['other', 'annet', 'annen']
};

export const LOCK_PROVIDERS = [
  { id: 'manual', label: 'Manuell / ukjent', chipColor: 'default', hasApiIntegration: false },
  { id: 'bekey', label: 'Bekey', chipColor: 'warning', hasApiIntegration: false },
  { id: 'unloc', label: 'Unloc', chipColor: 'info', hasApiIntegration: false },
  { id: 'puttekasse', label: 'Puttekasse', chipColor: 'primary', hasApiIntegration: false },
  { id: 'danalock', label: 'Danalock', chipColor: 'secondary', hasApiIntegration: false },
  { id: 'swedlock', label: 'Swedlock', chipColor: 'success', hasApiIntegration: false },
  { id: 'other', label: 'Annen', chipColor: 'default', hasApiIntegration: false }
];

const PROVIDER_BY_ID = Object.fromEntries(LOCK_PROVIDERS.map((entry) => [entry.id, entry]));

export function normalizeProvider(value) {
  if (value == null || value === '') {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (PROVIDER_BY_ID[normalized]) {
    return normalized;
  }

  for (const [providerId, aliases] of Object.entries(VENDOR_ALIASES)) {
    if (aliases.some((alias) => normalized === alias || normalized.includes(alias))) {
      return providerId;
    }
  }

  return null;
}

export function resolveProvider(ocProvider, ocVendor) {
  if (ocProvider != null && ocProvider !== '') {
    if (typeof ocProvider === 'number' || /^\d+$/.test(String(ocProvider))) {
      return OPTION_VALUE_TO_ID[Number(ocProvider)] || 'manual';
    }

    const fromChoiceLabel = normalizeProvider(ocProvider);
    if (fromChoiceLabel) {
      return fromChoiceLabel;
    }
  }

  return normalizeProvider(ocVendor) || 'manual';
}

export function getProviderLabel(providerId) {
  return PROVIDER_BY_ID[providerId]?.label || providerId || 'Manuell / ukjent';
}

export function getProviderChipColor(providerId) {
  return PROVIDER_BY_ID[providerId]?.chipColor || 'default';
}

export function listProvidersForFilter() {
  return LOCK_PROVIDERS;
}

export function countLocksByProvider(locks, providerId) {
  return locks.filter((lock) => (lock.provider || 'manual') === providerId).length;
}

export function getDistinctProvidersFromLocks(locks) {
  const ids = new Set(locks.map((lock) => lock.provider || 'manual'));
  return LOCK_PROVIDERS.filter((entry) => ids.has(entry.id));
}

function parseCoordinate(value) {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function mapDataverseLock(lock) {
  const provider = resolveProvider(lock.oc_provider, lock.oc_vendor);
  const organizationName = lock.oc_projectorganizationname || null;
  const note =
    lock.oc_notat != null && String(lock.oc_notat).trim() !== ''
      ? String(lock.oc_notat)
      : null;

  return {
    id: lock.oc_lockid,
    lockId: lock.oc_lockid,
    name: lock.oc_name,
    location: lock.oc_beskrivelse || lock.oc_newcolumn || 'Ikke oppgitt',
    provider,
    providerLabel: getProviderLabel(provider),
    externalId: lock.oc_externalid || null,
    type: lock.oc_vendor || 'Elektronisk',
    status: lock.statuscode === 1 ? 'Aktiv' : 'Inaktiv',
    lastMaintenance: lock.createdon
      ? lock.createdon.split('T')[0]
      : new Date().toISOString().split('T')[0],
    batteryLevel: lock.oc_batterylevel || 'NotAvailable',
    canBeLocked:
      typeof lock.oc_canbelocked === 'boolean' ? lock.oc_canbelocked : null,
    imageUrl: lock.oc_imageurl || null,
    latitude: parseCoordinate(lock.oc_latitude),
    longitude: parseCoordinate(lock.oc_longitude),
    city: lock.oc_city || null,
    countryIso: lock.oc_countryiso || null,
    street: lock.oc_street || null,
    postalCode: lock.oc_postalcode || null,
    floor: lock.oc_floor || null,
    visitorInstructions: lock.oc_visitorinstructions || null,
    physicalLockId: lock.oc_physicallockid || null,
    lockConnectionId: lock.oc_lockconnectionid || null,
    privacyPolicy: lock.oc_privacypolicy || null,
    duplicateLock: Boolean(lock.oc_duplicatelock),
    vendorType: lock.oc_vendortype || null,
    locationSource: lock.oc_locationsource || null,
    unlocUpdatedOn: lock.oc_unlocupdatedon || null,
    unlocCreatedOn: lock.oc_unloccreatedon || null,
    organizationName,
    note,
    duplicateSourceLock: lock.oc_duplicatesourcelockid || lock.oc_duplicatesourcelockname || lock.oc_projectid
      ? {
          id: lock.oc_duplicatesourcelockid || null,
          name: lock.oc_duplicatesourcelockname || null,
          project: lock.oc_projectid || lock.oc_projectname || lock.oc_projectorganizationid || organizationName
            ? {
                id: lock.oc_projectid || null,
                name: lock.oc_projectname || null,
                organizationId: lock.oc_projectorganizationid || null,
                organizationName
              }
            : null
        }
      : null,
    assignedResidents: lock.oc_Lock_Innbygger?.map((a) => a.oc_innbyggerid) || []
  };
}
